import { parseInfo } from './uci.mjs';

// One lazy worker per mounted board. A search is drained before another begins:
// "isready" alone is NOT a search-cancellation barrier in the UCI protocol.
export default class EngineClient {
  constructor({ url, depth = 30, multiPv = 2, hash = 32, onInfo = () => {}, onStatus = () => {}, createWorker = url => new Worker(url), startupTimeout = 45000, stopTimeout = 5000, searchTimeout = 120000 }) {
    Object.assign(this, { url, depth, multiPv, hash, onInfo, onStatus, createWorker, startupTimeout, stopTimeout, searchTimeout });
    this.worker = null;
    this.phase = 'off';
    this.desired = null;
    this.active = null;
    this.revision = 0;
    this.disposed = false;
  }

  status(status, message = '', details = {}) { this.onStatus({ ...details, status, message }); }
  send(command) {
    if (!this.worker) return false;
    try {
      this.worker.postMessage(command);
      return true;
    } catch {
      this.fail('Engine communication failed. Toggle off and on to retry.');
      return false;
    }
  }

  watchSearch() {
    // This is a no-output timeout, not a total limit on deep analysis.
    if (this.searchTimeout > 0) {
      this.deadline(this.searchTimeout, 'Engine stopped responding. Toggle off and on to retry.');
    }
  }
  clearDeadline() { clearTimeout(this.timer); this.timer = null; }
  deadline(ms, message) {
    this.clearDeadline();
    this.timer = setTimeout(() => this.fail(message), ms);
  }

  analyze({ fen, position = `position fen ${fen}`, infinite = false }) {
    if (this.disposed) return;
    this.desired = {
      fen,
      position,
      infinite,
      revision: ++this.revision,
    };
    if (!this.worker) this.start();
    else if (this.phase === 'searching') this.stopSearch();
    else if (this.phase === 'idle') this.beginSearch();
  }

  start() {
    this.initialized = false;
    this.phase = 'booting';
    this.status('loading');
    try {
      const worker = this.createWorker(this.url);
      this.worker = worker;
      worker.onmessage = event => {
        if (this.worker !== worker || this.disposed || typeof event.data !== 'string') return;
        for (const line of event.data.split(/\r?\n/)) {
          if (this.worker !== worker || this.disposed) break;
          this.receive(line.trim());
        }
      };
      worker.onerror = event => {
        event?.preventDefault?.();
        if (this.worker !== worker || this.disposed) return;
        this.fail(this.phase === 'searching' || this.phase === 'stopping'
          ? 'Engine cannot analyze this position. Toggle off and on to retry.'
          : 'Engine could not load. Toggle off and on to retry.');
      };
      worker.onmessageerror = () => this.fail('Engine communication failed. Toggle it off and on to retry.');
      this.deadline(this.startupTimeout, 'Engine loading timed out. Toggle it off and on to retry.');
      this.send('uci');
    } catch (_) {
      this.fail('Engine unavailable in this browser. Moving and editing still work.');
    }
  }

  receive(line) {
    if (!this.worker || !line) return;
    if (/CRITICAL ERROR|Aborted\(|RuntimeError|failed to (?:load|fetch)/i.test(line)) {
      this.fail('Engine stopped unexpectedly. Check the position, then toggle it off and on.');
      return;
    }
    if (line === 'uciok' && this.phase === 'booting') {
      for (const command of [
        'setoption name UCI_AnalyseMode value true',
        `setoption name Hash value ${this.hash}`,
        `setoption name MultiPV value ${this.multiPv}`,
        'ucinewgame',
      ]) {
        if (!this.send(command)) return;
      }
      this.phase = 'readying';
      this.send('isready');
      return;
    }
    if (line === 'readyok' && this.phase === 'readying') {
      this.clearDeadline();
      this.initialized = true;
      this.phase = 'idle';
      this.beginSearch();
      return;
    }
    if (/^bestmove\s/.test(line) && ['searching', 'stopping'].includes(this.phase)) {
      this.clearDeadline();
      const finished = this.active;
      this.active = null;
      if (finished && this.desired?.revision === finished.revision) {
        this.phase = 'idle';
        this.desired = null;
        const noMoves = /^bestmove\s+(?:\(none\)|0000)(?:\s|$)/.test(line);
        this.status(noMoves ? 'no-moves' : 'complete', noMoves
          ? 'Engine cannot analyze this position'
          : '');
      } else {
        this.phase = 'readying';
        this.deadline(this.stopTimeout, 'Engine did not respond. Toggle it off and on to retry.');
        this.send('isready');
      }
      return;
    }
    if (this.phase !== 'searching' || !this.active || this.active.revision !== this.desired?.revision) return;
    if (/^info\s+(?!string\b)/.test(line)) this.watchSearch();
    const info = parseInfo(line, this.active.fen.split(' ')[1]);
    if (info && info.index < this.multiPv) this.onInfo({ ...info, fen: this.active.fen });
  }

  beginSearch() {
    if (!this.desired) { this.status('off'); return; }
    this.active = this.desired;
    this.phase = 'searching';
    this.status('analyzing');
    this.watchSearch();
    if (!this.send(this.active.position)) return;
    this.send(this.active.infinite ? 'go infinite' : `go depth ${this.depth}`);
  }

  stopSearch() {
    this.phase = 'stopping';
    this.deadline(this.stopTimeout, 'Engine did not stop. Toggle it off and on to retry.');
    this.send('stop');
  }

  pause() {
    this.desired = null;
    if (this.phase === 'searching') this.stopSearch();
    // Cancel downloads / compilation immediately if switched off during startup.
    else if (this.phase === 'booting' || this.phase === 'readying') this.release();
    this.status('off');
  }

  release() {
    this.clearDeadline();
    if (this.worker) {
      const worker = this.worker;
      this.worker = null;
      worker.onmessage = worker.onerror = worker.onmessageerror = null;
      worker.terminate();
    }
    this.active = null;
    this.phase = 'off';
  }

  fail(message) {
    const failureStage = this.initialized ? 'analysis' : 'load';
    this.release();
    this.desired = null;
    if (!this.disposed) this.status('error', message, { failureStage });
  }

  dispose() {
    this.disposed = true;
    this.desired = null;
    this.release();
  }
}
